<template>
  <b-col class="p-0 m-0 container__create-method">
    <div class="my-u5">
      <h5 v-if="enableMode" class="font-weight-bold text__method-component">
        {{ mode }} Method Components Topic
      </h5>
      <div v-if="mode === 'view'" class="d-flex justify-content-between">
        <h5 class="font-weight-bold">
          {{ methodComponent.title }}
        </h5>
        <b-button
          v-if="isOwner && !isPublished"
          class="button__edit"
          variant="primary"
          @click="onEditing"
        >
          Edit
        </b-button>
      </div>
    </div>

    <b-form class="form__create-method-component" @submit.prevent="submit">
      <!-- title -->
      <div v-if="enableForm.title">
        <label for="method-component-title" class="mb-3">
          Title
          <span class="text-danger">*</span>
        </label>

        <b-form-input
          id="method-component-title"
          v-model="$v.form.title.$model"
          :class="{
            'is-invalid': $v.form.title.$error
          }"
          class="input__topic-title text__topic-title"
          :disabled="mode === 'view' || isLoading"
        >
        </b-form-input>
        <b-form-invalid-feedback
          v-if="!$v.form.title.required"
          ref="input__error-required"
          class="text_feedback"
        >
          Title is required
        </b-form-invalid-feedback>
      </div>

      <!-- content -->
      <div v-if="enableForm.content">
        <label for="method-component-content" class="mb-3">
          Content
          <span class="text-danger">*</span>
        </label>

        <base-text-editor
          id="method-component-content"
          :key="enableMode"
          v-model="$v.form.content.$model"
          :show-toolbar="enableMode"
          placeholder="Insert your content"
          :class="{
            'text-editor__required': $v.form.content.$error
          }"
          class="input__topic-content text__topic-content"
          :disabled="mode === 'view' || isLoading"
        >
        </base-text-editor>
        <div v-if="$v.form.content.$error" ref="input__error-required" class="text_feedback">
          Content is required
        </div>
      </div>

      <!-- additional resources -->
      <div v-if="enableForm.additional_resource">
        <label for="method-additional-resources" class="mb-3">
          Additional Resources (Optional)
        </label>

        <base-text-editor
          id="method-additional-resources"
          :key="enableMode"
          v-model="$v.form.additional_resource.$model"
          :toolbar="toolbar"
          :show-toolbar="enableMode"
          :disabled="mode === 'view' || isLoading"
          class="input__topic-resources text__additional-resources"
          placeholder="Insert your Additional Resources"
        >
        </base-text-editor>
      </div>

      <!-- Attachment -->
      <div v-if="enableForm.attachments">
        <label for="attachments" class="mb-3"> Attachment (Optional) </label>
        <base-s3-uploader
          id="inputMethodAttachments"
          ref="attachmentUploader"
          v-model="$v.form.attachments.$model"
          class="input__method-attachments"
          :reference-meta="{ ...referenceMeta, field: 'attachments' }"
          :is-public="false"
          :disabled="mode === 'view' || isLoading"
        >
        </base-s3-uploader>
      </div>
      <div class="d-flex">
        <b-button v-if="enableMode" class="button__create-method" type="submit" variant="primary">
          <b-spinner v-if="isLoading" small> </b-spinner>
          <span v-else>
            {{ actionTitle }}
          </span>
        </b-button>

        <!-- delete button -->
        <div v-if="!enableMode && isDeletable && !isPublished">
          <b-button
            :class="`button__method-${actionButtonModal.toLowerCase()}`"
            class="button__delete-topic"
            variant="link"
            @click="activeModal()"
          >
            {{ actionButtonModal }}
          </b-button>
        </div>

        <!-- canceled button -->
        <div v-if="enableMode && isDeletable && !isPublished">
          <b-button
            :class="`button__method-${actionButtonModal.toLowerCase()}`"
            variant="link"
            @click="activeModal()"
          >
            {{ actionButtonModal }}
          </b-button>
        </div>
      </div>
    </b-form>
    <ModalConfirmDelete
      :show-modal="isShowModal"
      :action-title="actionButtonModal"
      :method-title="form.title"
      @confirmed="confirmCancel"
      @canceled="isShowModal = false"
    />
  </b-col>
</template>

<script>
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import BaseS3Uploader from '@/components/base-components/BaseS3Uploader';
import MethodComponents from '@/models/MethodComponent';
import ModalConfirmDelete from './ModalConfirmDelete.vue';
import { required } from 'vuelidate/lib/validators';
import Upload from '@/models/Upload';

export default {
  components: {
    BaseTextEditor,
    BaseS3Uploader,
    ModalConfirmDelete
  },
  props: {
    mode: {
      type: String,
      default: 'create'
    },
    methodComponent: {
      type: Object,
      default: () => new MethodComponents()
    },
    isDeletable: {
      type: Boolean,
      default: true
    },
    isPublished: {
      type: Boolean,
      default: false
    },
    isOwner: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      isShowModal: false,
      fileIdsTodelete: [],
      tempIndex: null,
      isChangeCancel: false,
      form: {
        title: '',
        content: '',
        additional_resource: '',
        attachments: []
      },
      referenceMeta: {
        ref: 'method-component',
        refId: null
      },
      enableForm: {
        title: true,
        content: true,
        additional_resource: true,
        attachments: true
      },
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ header: '1' }, { header: '2' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link']
      ]
    };
  },
  validations: {
    form: {
      title: {
        required
      },
      content: {
        required
      },
      reference: {},
      additional_resource: {},
      attachments: {}
    }
  },
  computed: {
    actionTitle() {
      return this.mode === 'create' ? 'Create' : 'Save';
    },
    actionButtonModal() {
      return this.mode === 'create' || this.mode === 'edit' ? 'Cancel' : 'Delete';
    },
    enableMode() {
      return this.mode === 'create' || this.mode === 'edit';
    }
  },
  watch: {
    mode: {
      immediate: true,
      handler(mode) {
        if (mode === 'edit') {
          Object.keys(this.enableForm).forEach((key) => (this.enableForm[key] = true));
        }
      }
    },

    methodComponent: {
      deep: true,
      immediate: true,
      handler() {
        this.initFormData();
      }
    }
  },

  methods: {
    initFormData() {
      this.resetForm();
      this.$set(this, 'form', {
        title: this.methodComponent?.title,
        content: this.methodComponent?.content,
        additional_resource: this.methodComponent?.additional_resource,
        attachments: this.methodComponent?.attachments
      });

      Object.keys(this.form).forEach((key) => {
        if (this.mode === 'view') {
          this.$set(this.enableForm, key, !!this.form[key]?.length);
        } else if (['edit', 'create'].includes(this.mode)) {
          this.$set(this.enableForm, key, this.form[key] ?? true);
        }
      });
      this.isChangeCancel = false;

      this.$nextTick(() => this.$v.$reset());
    },

    deleteFile(index) {
      const file = this.form.attachments[index];
      if (file && file.id) {
        this.fileIdsTodelete.push(file);
        this.form.attachments.splice(index, 1);
      } else {
        this.form.attachments.splice(index, 1);
      }
    },
    changeCancel(index) {
      this.tempIndex = index;
      this.activeModal();
      this.isChangeCancel = true;
    },

    activeModal() {
      this.isShowModal = true;
    },

    confirmCancel(actionTitle) {
      this.isShowModal = false;

      if (this.mode === 'view' && actionTitle === 'Delete') {
        this.$emit('delete', { id: this.methodComponent.id, index: this.tempIndex });
      } else if (this.isChangeCancel) {
        this.$emit('change-cancel', this.tempIndex);
      } else {
        this.resetForm();
        this.initFormData();
        this.$emit('canceled');
      }

      this.isChangeCancel = false;
    },

    async submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.isLoading = true;

      const data = {
        ...this.form
      };

      this.$emit('submit:method-component', data);
      this.isLoading = false;
    },
    async uploadFiles(relatedId) {
      let data = {
        ...this.form
      };
      this.$set(this.referenceMeta, 'refId', relatedId);
      let attachments = [];

      if (this.$refs.attachmentUploader.hasAnyDeletedFiles()) {
        await this.$refs.attachmentUploader.deleteRemovedFiles();
      }
      if (this.$refs.attachmentUploader.hasAnyQueuedFiles()) {
        attachments = await this.$refs.attachmentUploader.upload();
      }
      if (this.fileIdsTodelete && this.fileIdsTodelete.length) {
        await this.removeFiles(this.fileIdsTodelete);
      }
      data.attachments = data.attachments ? data.attachments.concat(attachments) : attachments;
      this.fileIdsTodelete = [];

      this.$v.$reset();
      this.isLoading = false;

      return data.attachments;
    },

    async resetForm() {
      Object.assign(this.form, {
        title: '',
        content: '',
        additional_resource: '',
        attachments: []
      });

      this.$nextTick(() => {
        if (this.$refs.attachmentUploader) {
          this.$refs.attachmentUploader.buffer = [];
        }
        this.$v.$reset();
      });
    },

    onEditing() {
      this.$emit('editing');
    },

    async removeFiles(files) {
      for (const file of files) {
        try {
          await Upload.api().deleteFile(file.id);
        } catch (error) {
          console.error(error);
          throw error;
        }
      }
    }
  }
};
</script>

<style lang="scss">
.container__create-method {
  .text__method-component {
    text-transform: capitalize;
  }
  .form__create-method-component {
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 720px;
    .button__delete-method {
      color: var(--danger);
    }
  }

  .text-editor__required {
    .ql-container.ql-snow {
      background: #ffffff;
      border: 1px solid #f83245;
      border-radius: 0 0 4.64px 4.64px;
    }

    .ql-toolbar.ql-snow {
      background: #ffffff;
      border: 1px solid #f83245;
      border-radius: 4.64px 4.64px 0 0;
      border-bottom: none;
    }
  }

  .text_feedback {
    width: 100%;
    margin-top: 0.25rem;
    font-size: 80%;
    color: #f83245;
  }
}

.v-toast-success {
  .v-toast-text {
    a.link__preview-as-learner {
      color: var(--white);
      font-weight: 700;
      text-decoration: underline;
    }
  }
}

.button__create-method {
  display: flex;
  align-items: center;
}

.button__method-delete {
  color: var(--danger);
}
</style>
